import {
  USER_GET_PROFILE_SETTINGS,
  USER_REMOVE_PROFILE_SETTINGS,
  USER_RESET_PROFILE_SETTINGS,
  USER_SAVE_PROFILE_SETTINGS,
  USER_SET_CHANNEL_PROFILE_SETTING,
  USER_SET_PROFILE_SETTING,
  USER_SET_PROFILE_SETTINGS
} from 'common/constants/action-types';
import { minutesToMS } from 'common/services/helpers';

export enum CLOCK_POSITIONS {
  IN_MENU = 'in-menu',
  ALWAYS = 'always'
}

export enum LOCK_PERIODS {
  ALWAYS_LOCK = -1,
  SESSION_LOCK = -2
}

/* Canonical audio/subtitle/resolution values, mirroring ott-objects LangPref /
 * Resolution. Writes use only these; reads also accept the legacy -1/null forms
 * (see normalizeReactTVSettings). */
export const LANG_DEFAULT = 'und'; // use the default audio/subtitle track
export const SUBTITLES_OFF = 'zxx'; // subtitles off (no linguistic content)
export const RESOLUTION_AUTO = 'auto'; // adaptive; otherwise "WIDTHxHEIGHT"

export type LanguageSetting = string | null | undefined;

export type ChannelSettings = { [id: string]: ChannelSetting };

export interface ChannelSetting {
  favoritePosition: number;
  favorite?: boolean;
  visible?: boolean;
  audio: LanguageSetting;
  resolution: string | null; // "auto" | "WIDTHxHEIGHT"; null = inherit global
  subtitles: LanguageSetting;
  position: number | null;
  locked?: boolean;
}
export type ChannelSettingKeys = keyof ChannelSetting;
export type ChannelSettingValues = ChannelSetting[ChannelSettingKeys];

export interface Settings {
  resolution: string; // "auto" | "WIDTHxHEIGHT"
  subtitles: string; // ISO 639-2/B code | "und" default | "zxx" off
  audio: string; // ISO 639-2/B code | "und" default
  channels: ChannelSettings;
  lockPeriod: LOCK_PERIODS | number;
  timeToLockChannels: null;
  clockPosition: CLOCK_POSITIONS;
  saved?: boolean;
}

export type SettingsKey = keyof Settings;

export interface ProfileSettings {
  react_tv_settings: Settings;
}

export const INITIAL_STATE: ProfileSettings = {
  react_tv_settings: {
    resolution: RESOLUTION_AUTO,
    subtitles: LANG_DEFAULT,
    audio: LANG_DEFAULT,
    channels: {},
    lockPeriod: minutesToMS(5),
    timeToLockChannels: null,
    clockPosition: CLOCK_POSITIONS.IN_MENU,
    saved: false
  }
};

export default function (state = INITIAL_STATE, action: any) {
  switch (action.type) {
    case USER_GET_PROFILE_SETTINGS:
      return {
        ...state,
        ...action.payload,
        react_tv_settings: {
          ...state.react_tv_settings,
          ...action.payload.react_tv_settings,
          saved: true
        }
      };

    case USER_SET_PROFILE_SETTINGS:
      return {
        ...state,
        ...action.payload,
        react_tv_settings: {
          ...state.react_tv_settings,
          ...action.payload.react_tv_settings,
          saved: false
        }
      };
    case USER_SAVE_PROFILE_SETTINGS:
      return {
        ...state,
        ...action.payload,
        react_tv_settings: {
          ...state.react_tv_settings,
          saved: true
        }
      };
    case USER_SET_PROFILE_SETTING: {
      const { key, value } = action.payload;
      return {
        ...state,
        react_tv_settings: {
          ...state.react_tv_settings,
          [key]: value,
          saved: false
        }
      };
    }
    case USER_SET_CHANNEL_PROFILE_SETTING: {
      const channel = state.react_tv_settings.channels[action.payload.id];
      if (!channel) {
        return state;
      }
      channel[action.payload.key] = action.payload.value;

      return {
        ...state,
        react_tv_settings: {
          ...state.react_tv_settings,
          channels: {
            ...state.react_tv_settings.channels,
            [action.payload.id]: { ...channel }
          },
          saved: false
        }
      };
    }
    case USER_REMOVE_PROFILE_SETTINGS:
      return {
        ...action.payload
      };
    case USER_RESET_PROFILE_SETTINGS:
      return {
        ...state,
        react_tv_settings: {
          ...state.react_tv_settings,
          ...action.payload
        }
      };
    default:
      return state;
  }
}
